import { z } from 'zod';
import { uuidSchema } from './validation.js';
/**
 * Client prices and services of a 3PL (BLU-770 step 2, Products > Client
 * prices). Inputs only: the organisation and the user always come from auth,
 * so no schema here carries an organizationId (zod's default strip drops one).
 *
 * Every save carries only the NEW value of each changed key, never the value
 * the browser loaded: last write wins (D38). A CSV import is the same save
 * with a filename and the file's row count, SKU prices only (S2). The two
 * hand-added bill line inputs (3PL usage report) live here too.
 */
/** A price as typed in the grid or a CSV cell: up to 6 whole digits, at most 2 decimals. */
export const CLIENT_PRICE_REGEX = /^\d{1,6}(\.\d{1,2})?$/;
export const CLIENT_PRICE_MAX = 999999.99;
// LOCKSTEP: save_three_pl_prices caps in
// supabase/migrations/20261011150000_blu770_client_prices.sql (D53).
export const CLIENT_PRICE_SAVE_MAX_PRICES = 10000;
export const CLIENT_PRICE_SAVE_MAX_SWITCHES = 1000;
export const THREE_PL_SERVICE_NAME_MAX = 80;
export const THREE_PL_BILL_NOTE_MAX = 200;
export const THREE_PL_BILL_QTY_MAX = 100000;
export const THREE_PL_CHARGE_PER = ['piece', 'order', 'flat'];
export const threePlChargePerSchema = z.enum(THREE_PL_CHARGE_PER);
/** Printing and Pick and pack: priced like services, never renamed or removed (D14). */
export const THREE_PL_BUILTIN_KEYS = ['printing', 'pick_pack'];
export const threePlBuiltinKeySchema = z.enum(THREE_PL_BUILTIN_KEYS);
/**
 * Whole cents of a money value: THE one money rounding (D47). Every money sum
 * and comparison runs in these, in the browser (Client prices, the 3PL bill)
 * and on the server (the usage report's checks), never on floats.
 */
export const toCents = (value) => Math.round(value * 100);
const MAX_CENTS = toCents(CLIENT_PRICE_MAX);
/** Money: a number whose cents are a whole number from 0 to 99,999,999 (numeric(12,2) in SQL). */
export const clientPriceAmountSchema = z
    .number()
    .finite()
    .refine((value) => {
    const cents = toCents(value);
    return Math.abs(value * 100 - cents) < 1e-6 && cents >= 0 && cents <= MAX_CENTS;
}, { message: 'Use a price from 0 to 999999.99 with at most two decimals' });
/** What a price is for: one of the 3PL's SKUs, one of its own services, or a built-in. */
export const clientPriceItemSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('sku'), skuId: uuidSchema }),
    z.object({ kind: z.literal('service'), serviceId: uuidSchema }),
    z.object({ kind: z.literal('builtin'), key: threePlBuiltinKeySchema }),
]);
/** One changed price. clientOrgId null = All clients; to null = clear the price. */
export const clientPriceEditSchema = z.object({
    clientOrgId: uuidSchema.nullable(),
    item: clientPriceItemSchema,
    to: clientPriceAmountSchema.nullable(),
});
/** One changed turn-on state of an own service for one client (never All clients, D17). */
export const threePlServiceSwitchEditSchema = z.object({
    clientOrgId: uuidSchema,
    serviceId: uuidSchema,
    to: z.boolean(),
});
export const saveClientPricesSchema = z
    .object({
    prices: z.array(clientPriceEditSchema).max(CLIENT_PRICE_SAVE_MAX_PRICES),
    switches: z.array(threePlServiceSwitchEditSchema).max(CLIENT_PRICE_SAVE_MAX_SWITCHES),
    /** A CSV import: the uploaded file name, shown in Logs > Bulk imports. */
    filename: z.string().trim().min(1).max(255).optional(),
    /** A CSV import: the number of rows in the file. */
    itemCount: z.number().int().min(1).max(CLIENT_PRICE_SAVE_MAX_PRICES).optional(),
})
    .superRefine((value, context) => {
    if (value.prices.length + value.switches.length === 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'There is nothing to save', path: ['prices'] });
    }
    if ((value.filename === undefined) !== (value.itemCount === undefined)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A price import needs both the file name and its row count',
            path: ['filename'],
        });
    }
    if (value.filename !== undefined) {
        if (value.switches.length > 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'A price import changes SKU prices only',
                path: ['switches'],
            });
        }
        if (value.prices.some((edit) => edit.item.kind !== 'sku')) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'A price import changes SKU prices only',
                path: ['prices'],
            });
        }
    }
});
const serviceNameSchema = z
    .string()
    .trim()
    .min(1, 'Give the service a name')
    .max(THREE_PL_SERVICE_NAME_MAX, `Use ${THREE_PL_SERVICE_NAME_MAX} characters or fewer`);
/** Add an own service, with its optional All clients price, in one call. */
export const addThreePlServiceSchema = z.object({
    name: serviceNameSchema,
    chargePer: threePlChargePerSchema,
    price: clientPriceAmountSchema.nullable().optional(),
});
/** Rename and/or remove (archive) an own service; at least one of the two. */
export const updateThreePlServiceSchema = z
    .object({
    serviceId: uuidSchema,
    name: serviceNameSchema.optional(),
    archive: z.boolean().optional(),
})
    .refine((value) => value.name !== undefined || value.archive === true, {
    message: 'Rename or remove the service',
    path: ['name'],
});
// LOCKSTEP: add_three_pl_bill_line's qty and note checks in
// supabase/migrations/20261012023000_blu770_3pl_bill_money.sql (D36, D48).
/**
 * Add an own service to one client's bill at one location by hand. The line is
 * dated the location's today in SQL, never by the browser (D3); the note is
 * visible to the client (D36).
 */
export const addThreePlBillLineSchema = z.object({
    locationId: uuidSchema,
    clientOrgId: uuidSchema,
    serviceId: uuidSchema,
    qty: z
        .number()
        .int('Enter a whole number')
        .min(1, 'Enter how many, like 40')
        .max(THREE_PL_BILL_QTY_MAX, `Use ${THREE_PL_BILL_QTY_MAX} or fewer`),
    note: z
        .string()
        .trim()
        .max(THREE_PL_BILL_NOTE_MAX, `Use ${THREE_PL_BILL_NOTE_MAX} characters or fewer`)
        .nullable()
        .optional(),
});
/** Remove one hand-added line from a bill (soft delete). */
export const deleteThreePlBillLineSchema = z.object({
    lineId: uuidSchema,
});
